import { z } from "zod";

import { APIResponseSuccessSchema } from "@/schema/api";

export type StatIconKey = "BarChart3" | "AlertTriangle" | "Film" | "Clock";

export interface StatCardData {
  iconKey: StatIconKey;
  value: string;
  primaryLabel: string;
  secondaryLabel: string;
  delta?: string;
}

export type AlertSeverity = "high" | "medium" | "low";

export interface TrendingItem {
  id: string;
  label: string;
  value: string;
  isPositive: boolean;
}

export type PipelineStatus = "briefed" | "in_production" | "in_review" | "approved" | "rejected";

export interface PipelineStage {
  id: PipelineStatus;
  label: string;
  count: number;
  barColor: string;
  legendColor: string;
}

export interface DimensionScore {
  id: string;
  label: string;
  achieved: number;
  total: number;
}

export interface SubBrandComplianceItem {
  id: string;
  name: string;
  score: number;
}

export interface BenchmarkStatItem {
  id: string;
  title: string;
  value: string;
  percentOfLeader: number;
  leaderValue: string;
}

export interface BrandBarItem {
  brand: string;
  value: number;
}

export interface ShareOfVoiceItem {
  brand: string;
  share: number;
}

export interface ChannelGapItem {
  channel: string;
  leader: number;
  bata: number;
}

export interface CreativeMonitorStatItem {
  label: string;
  value: number;
  valueClassName: string;
}

export type BrandCheckStatus = "approved" | "flagged" | "review";

export interface BrandCheckItem {
  id: string;
  timeAgo: string;
  brandName: string;
  brandColor: string;
  platform: string;
  contentType: string;
  score: number;
  status: BrandCheckStatus;
  description: string;
  dimensionScores: Record<string, number>;
  url: string;
}

// ── GetBrandOverview API ───────────────────────────────────────────────────

export const BrandOverviewSegmentSchema = z.object({
  name: z.string(),
  sub_brands: z.array(z.string()),
});

export const BrandOverviewDataSchema = z.object({
  brand_name: z.string(),
  segments: z.array(BrandOverviewSegmentSchema),
  channels: z.array(z.string()),
});

export const BrandOverviewResponseSchema = APIResponseSuccessSchema.extend({
  data: BrandOverviewDataSchema,
});

export type BrandOverviewResponse = z.infer<typeof BrandOverviewResponseSchema>;
export type BrandOverviewData = z.infer<typeof BrandOverviewDataSchema>;
export type BrandOverviewSegment = z.infer<typeof BrandOverviewSegmentSchema>;

// ── GetCreativesScorecard API ──────────────────────────────────────────────

const GovernanceScoreMetricSchema = z.object({
  score: z.number().nullable().optional(),
  prev_score: z.number().nullable().optional(),
  pct_change: z.number().nullable().optional(),
  diff: z.number().nullable().optional(),
});

const CreativesScorecardPeriodSchema = z.object({
  current: z.object({ start_date: z.string().nullable(), end_date: z.string().nullable() }),
  previous: z.object({ start_date: z.string().nullable(), end_date: z.string().nullable() }),
  type: z.string().nullable(),
});

const CreativesScorecardFiltersAppliedSchema = z.object({
  channels: z.array(z.string()).nullable(),
  segments: z.array(z.string()).nullable(),
  sub_brands: z.array(z.string()).nullable(),
});

const CreativesScorecardDimensionSummarySchema = z.object({
  visual_identity: GovernanceScoreMetricSchema,
  color_palette: GovernanceScoreMetricSchema,
  logo_usage: GovernanceScoreMetricSchema,
  typography: GovernanceScoreMetricSchema,
  tone_of_voice: GovernanceScoreMetricSchema,
  composition: GovernanceScoreMetricSchema,
});

const ScoreTrendDailyItemSchema = z.object({
  date: z.string().nullable().optional(),
  prev_date: z.string().nullable().optional(),
  score: z.number().nullable().optional(),
  prev_score: z.number().nullable().optional(),
  pct_change: z.number().nullable().optional(),
  diff: z.number().nullable().optional(),
});

export const CreativesScorecardScoreTrendSchema = z.object({
  daily: z.array(ScoreTrendDailyItemSchema),
  period_avg: GovernanceScoreMetricSchema,
});

const CreativesScorecardComplianceItemSchema = z.object({
  sub_brand: z.string(),
  channel: z.string(),
  score: z.number().nullable().optional(),
  prev_score: z.number().nullable().optional(),
  pct_change: z.number().nullable().optional(),
  diff: z.number().nullable().optional(),
  status: z.string().nullable().optional(),
  prev_status: z.string().nullable().optional(),
});

const CreativesScorecardSubBrandOverviewItemSchema = z.object({
  sub_brand: z.string(),
  score: z.number().nullable().optional(),
  prev_score: z.number().nullable().optional(),
  pct_change: z.number().nullable().optional(),
  diff: z.number().nullable().optional(),
  num_issues: z.number().nullable(),
  prev_num_issues: z.number().nullable().optional(),
  total_creatives: z.number().optional(),
  prev_total_creatives: z.number().optional(),
});

const FormatBreakdownValuesSchema = z.object({
  video: z.number(),
  static: z.number(),
  image: z.number(),
  text: z.number(),
  unknown: z.number(),
  video_ratio: z.number(),
});

const CreativesScorecardFormatBreakdownSchema = z.object({
  breakdown: FormatBreakdownValuesSchema,
  prev_breakdown: FormatBreakdownValuesSchema.optional(),
});

const CreativesScorecardBrandScorecardOverallScoreSchema = z.object({
  score: z.number().nullable().optional(),
  prev_score: z.number().optional(),
  pct_change: z.number().nullable().optional(),
  diff: z.number().nullable().optional(),
});

const CreativesScorecardBrandScorecardCountSchema = z.object({
  count: z.number(),
  prev_count: z.number().nullable().optional(),
});

const CreativesScorecardBrandScorecardTimeToPublishSchema = z.object({
  value: z.number(),
  prev_value: z.number().nullable().optional(),
  pct_change: z.number().nullable().optional(),
  diff: z.number().nullable().optional(),
});

const CreativesScorecardChannelBreakdownRowSchema = z.object({
  channel: z.string(),
  total: z.number(),
  video: z.number(),
  static: z.number(),
  image: z.number(),
  text: z.number(),
  avg_score: z.number(),
  time_to_publish: z.number(),
});

const CreativesScorecardChannelBreakdownSchema = z.object({
  breakdown: z.array(CreativesScorecardChannelBreakdownRowSchema),
  prev: z.array(z.unknown()),
});

export const CreativesScorecardBrandScorecardSchema = z.object({
  overall_score: CreativesScorecardBrandScorecardOverallScoreSchema,
  below_threshold_count: CreativesScorecardBrandScorecardCountSchema,
  total_creatives: CreativesScorecardBrandScorecardCountSchema,
  format_breakdown: CreativesScorecardFormatBreakdownSchema,
  channel_breakdown: CreativesScorecardChannelBreakdownSchema,
  time_to_publish: CreativesScorecardBrandScorecardTimeToPublishSchema,
});

export type CreativesScorecardBrandScorecard = z.infer<typeof CreativesScorecardBrandScorecardSchema>;

const CreativesScorecardTrendItemParamsSchema = z.object({
  entity: z.string(),
  metric: z.string(),
  direction: z.enum(["improved", "declined"]),
  pct_change: z.number().nullable(),
  diff: z.number().nullable().optional(),
});
const CreativesScorecardTrendItemSchema = z.object({
  text: z.string().optional(),
  params: CreativesScorecardTrendItemParamsSchema,
});

const CreativesScorecardTrendsSchema = z.object({
  brand: z.array(CreativesScorecardTrendItemSchema),
  sub_brand: z.array(CreativesScorecardTrendItemSchema),
  sub_brand_channel: z.array(CreativesScorecardTrendItemSchema),
});

const CreativesScorecardDataSchema = z.object({
  brand_name: z.string(),
  period: CreativesScorecardPeriodSchema,
  filters_applied: CreativesScorecardFiltersAppliedSchema,
  score_trend: CreativesScorecardScoreTrendSchema,
  dimension_summary: CreativesScorecardDimensionSummarySchema,
  compliance_matrix: z.array(CreativesScorecardComplianceItemSchema),
  sub_brand_overview: z.array(CreativesScorecardSubBrandOverviewItemSchema).nullable(),
  brand_scorecard: CreativesScorecardBrandScorecardSchema,
  trends: CreativesScorecardTrendsSchema,
});

export const CreativesScorecardResponseSchema = APIResponseSuccessSchema.extend({
  data: CreativesScorecardDataSchema,
});

export type CreativesScorecardResponse = z.infer<typeof CreativesScorecardResponseSchema>;
export type CreativesScorecardData = z.infer<typeof CreativesScorecardDataSchema>;
export type CreativesScorecardTrends = z.infer<typeof CreativesScorecardTrendsSchema>;
export type CreativesScorecardTrendItem = z.infer<typeof CreativesScorecardTrendItemSchema>;
export type CreativesScorecardSubBrandOverviewItem = z.infer<typeof CreativesScorecardSubBrandOverviewItemSchema>;

// ── GetLiveMonitor (Brand Monitor) API ─────────────────────────────────────

export const CreativeLabelSchema = z.enum(["Good", "Requires Review", "Flagged for Improvement"]);
export type CreativeLabel = z.infer<typeof CreativeLabelSchema>;

export const CreativeFormatSchema = z.enum(["Image", "Video", "Text"]);
export type CreativeFormat = z.infer<typeof CreativeFormatSchema>;

export const LiveMonitorSeveritySchema = z.enum(["high", "medium", "low"]);

const LiveMonitorDimensionScoresSchema = z.object({
  visual_identity: z.number(),
  color_palette: z.number(),
  logo_usage: z.number(),
  typography: z.number(),
  tone_of_voice: z.number(),
  composition: z.number(),
});

export type DimensionScores = z.infer<typeof LiveMonitorDimensionScoresSchema>;

const LiveMonitorCreativeSchema = z.object({
  platform_id: z.string(),
  sub_brand: z.string(),
  platform: z.string(),
  platform_serving: z.string(),
  format: CreativeFormatSchema,
  score: z.number(),
  label: CreativeLabelSchema,
  url: z.string(),
  is_active: z.boolean(),
  ai_verdict: z.string(),
  dimension_scores: LiveMonitorDimensionScoresSchema,
  positives: z.array(z.string()),
  negatives: z.array(z.string()),
  summary_line: z.string(),
});

export type Creative = z.infer<typeof LiveMonitorCreativeSchema>;

const LiveMonitorAlertCreativeSchema = z.object({
  platform_id: z.string(),
  sub_brand: z.string(),
  platform: z.string(),
  format: CreativeFormatSchema,
  url: z.string(),
  overall_score: z.number(),
  dimension_score: z.number(),
  severity: LiveMonitorSeveritySchema,
  negatives: z.array(z.string()),
  summary: z.string(),
});

export type AlertCreative = z.infer<typeof LiveMonitorAlertCreativeSchema>;

const LiveMonitorAlertSchema = z.object({
  severity: LiveMonitorSeveritySchema,
  sub_brand: z.string(),
  platform: z.string(),
  dimension: z.string(),
  title: z.string(),
  count: z.number(),
  avg_score: z.number(),
  creatives: z.array(LiveMonitorAlertCreativeSchema),
});

export type Alert = z.infer<typeof LiveMonitorAlertSchema>;

const LiveMonitorMonitorSchema = z.object({
  total: z.number(),
  good: z.number(),
  requires_review: z.number(),
  flagged_for_improvement: z.number(),
});

export type MonitorSummary = z.infer<typeof LiveMonitorMonitorSchema>;

const LiveMonitorDataSchema = z.object({
  brand_name: z.string(),
  date: z.string(),
  monitor: LiveMonitorMonitorSchema,
  creatives: z.array(LiveMonitorCreativeSchema),
  alerts: z.array(LiveMonitorAlertSchema),
});

export type LiveMonitorData = z.infer<typeof LiveMonitorDataSchema>;

export const LiveMonitorResponseSchema = APIResponseSuccessSchema.extend({
  data: LiveMonitorDataSchema,
});

export type BrandMonitorResponse = z.infer<typeof LiveMonitorResponseSchema>;
export type LiveMonitorResponse = BrandMonitorResponse;

export type LiveMonitorCreative = Creative;
export type LiveMonitorAlertCreative = AlertCreative;
export type LiveMonitorAlert = Alert;
export type LiveMonitorMonitor = MonitorSummary;

// ── GetCompetitorBenchmark API ─────────────────────────────────────────────

const CompetitorBenchmarkPeriodSchema = z.object({
  current: z.object({ start_date: z.string(), end_date: z.string() }),
  previous: z.object({ start_date: z.string(), end_date: z.string() }),
  type: z.string(),
});

const CompetitorBenchmarkVelocityRankingSchema = z.object({
  brand: z.string(),
  count: z.number(),
  prev_count: z.number(),
  pct_change: z.number(),
  rank: z.number(),
});

const CompetitorBenchmarkVelocitySchema = z.object({
  rankings: z.array(CompetitorBenchmarkVelocityRankingSchema),
  brand_value: z.object({ count: z.number(), prev_count: z.number() }),
  leader: z.object({ brand: z.string(), count: z.number() }),
  gap_to_leader: z.number(),
});

const CompetitorBenchmarkVideoProductionRankingSchema = z.object({
  brand: z.string(),
  count: z.number(),
  prev_count: z.number(),
  pct_change: z.number(),
  rank: z.number(),
});

const CompetitorBenchmarkVideoProductionSchema = z.object({
  rankings: z.array(CompetitorBenchmarkVideoProductionRankingSchema),
  brand_value: z.object({ count: z.number(), prev_count: z.number() }),
  leader: z.object({ brand: z.string(), count: z.number() }),
  gap_to_leader: z.number(),
});

const CompetitorBenchmarkComplianceRankingSchema = z.object({
  brand: z.string(),
  score: z.number(),
  prev_score: z.number(),
  pct_change: z.number(),
  rank: z.number(),
});

const CompetitorBenchmarkComplianceSchema = z.object({
  rankings: z.array(CompetitorBenchmarkComplianceRankingSchema),
  brand_value: z.object({ score: z.number(), prev_score: z.number() }),
  leader: z.object({ brand: z.string(), score: z.number() }),
  gap_to_leader: z.number(),
});

const CompetitorBenchmarkChannelRankingSchema = z.object({
  brand: z.string(),
  avg_score: z.number(),
  prev_avg_score: z.number(),
  total: z.number(),
  prev_total: z.number(),
  rank: z.number(),
  pct_change: z.number(),
});

const CompetitorBenchmarkChannelMetricSchema = z.object({
  rankings: z.array(CompetitorBenchmarkChannelRankingSchema),
  brand_value: z.object({ avg_score: z.number(), total: z.number() }),
  leader: z.object({ brand: z.string(), avg_score: z.number() }),
  gap_to_leader: z.number(),
});

const CompetitorBenchmarkDataSchema = z.object({
  brand_name: z.string(),
  competitors: z.array(z.string()),
  period: CompetitorBenchmarkPeriodSchema,
  benchmark: z.object({
    velocity: CompetitorBenchmarkVelocitySchema,
    video_production: CompetitorBenchmarkVideoProductionSchema,
    compliance: CompetitorBenchmarkComplianceSchema,
    channel_gap_to_leader: z.record(z.string(), CompetitorBenchmarkChannelMetricSchema),
  }),
});

export const CompetitorBenchmarkResponseSchema = APIResponseSuccessSchema.extend({
  data: CompetitorBenchmarkDataSchema,
});

export type CompetitorBenchmarkResponse = z.infer<typeof CompetitorBenchmarkResponseSchema>;
export type CompetitorBenchmarkData = z.infer<typeof CompetitorBenchmarkDataSchema>;
